// C:\Sites\cartman\src\components\FullCalendar\FullCalendarWithModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AiOutlineCalendar,
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlineFilter,
  AiOutlineClose,
  AiOutlineEdit,
  AiOutlineWarning,
} from "react-icons/ai";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";

import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
  Spinner,
  Select,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Checkbox,
  Stack,
  Avatar,
  Textarea,
} from "@chakra-ui/react";

import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import api from "../../services/api";
import { useAuth } from "../../hooks/auth";



import {
  ISO_FMT,
  getMaxDaysForEmployee,
  fmtBR,
  formatViewTitle,
  getAdmissionCycleWindow,
  inclusiveDaysCount,
  overlapDaysInclusive,
  stripEmailDomain,
  toISO,
  getCategoryColor,
  isSprintCategory,
  isFeriasCategory,
  getSprintNumber,
  getIsAdminFromRecord,
} from "./FullCalendarUtils";

import { getHolidayByDate } from "./Holidays";

import "../../styles/fullcalendarCustom.css";

// =========================================================
// IMPORTANT:
// Backend is returning END as EXCLUSIVE (FullCalendar style).
// UI/Modal must work with END as INCLUSIVE.
// =========================================================
const normalizeBackendEndToInclusive = (startISO, endISO) => {
  const s = moment(startISO, ISO_FMT, true);
  const e = moment(endISO, ISO_FMT, true);

  if (!s.isValid()) return { startISO: null, endISO: null };
  if (!e.isValid()) return { startISO, endISO: startISO };

  // If backend end is after start, treat it as EXCLUSIVE and subtract 1 day
  if (e.isAfter(s, "day")) {
    const inclusive = e.clone().subtract(1, "day").format(ISO_FMT);
    // Clamp (never before start)
    if (moment(inclusive, ISO_FMT, true).isBefore(s, "day")) return { startISO, endISO: startISO };
    return { startISO, endISO: inclusive };
  }

  // Same-day or weird cases -> clamp
  if (e.isBefore(s, "day")) return { startISO, endISO: startISO };
  return { startISO, endISO: startISO };
};

function sanitizeComment(str) {
  if (!str) return "";
  return String(str).trim().slice(0, 500);
}

export default function FullCalendarWithModal() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const calendarRef = useRef(null);
  const stageRef = useRef(null);

  const todayISO = useMemo(() => moment().format(ISO_FMT), []);
  const calendarApi = () => calendarRef.current?.getApi?.();

  const [me, setMe] = useState(null);
  const isAdmin = getIsAdminFromRecord(me);

  const [events, setEvents] = useState([]);
  const [viewTitle, setViewTitle] = useState("");

  const [eventsLoading, setEventsLoading] = useState(true);

  const { isOpen: createOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [category, setCategory] = useState("Férias");
  const [comment, setComment] = useState("");
  const [modalError, setModalError] = useState("");

  // Sprint Creation State
  const { isOpen: createSprintOpen, onOpen: onCreateSprintOpen, onClose: onCreateSprintClose } = useDisclosure();
  const [sprintStartDate, setSprintStartDate] = useState("");
  const [sprintEndDate, setSprintEndDate] = useState("");
  const [sprintNumber, setSprintNumber] = useState(74);
  const [sprintComment, setSprintComment] = useState("");
  const [sprintEmployeePick, setSprintEmployeePick] = useState("");
  const [sprintEmployeePickId, setSprintEmployeePickId] = useState(null);
  const [sprintModalError, setSprintModalError] = useState("");
  const { isOpen: sprintWarnOpen, onOpen: onSprintWarnOpen, onClose: onSprintWarnClose } = useDisclosure();
  const [sprintWarnMessages, setSprintWarnMessages] = useState([]);

  const openCreateSprintModalFor = (startISO, endISO) => {
    setSprintStartDate(startISO);
    setSprintEndDate(endISO);
    // Sugere a próxima virada com base na sprint mais recente visível no calendário
    setSprintNumber(latestSprintNum > 0 ? latestSprintNum + 1 : 74);
    setSprintComment("");
    setSprintModalError("");
    setSprintWarnMessages([]);
    if (me) {
      setSprintEmployeePick(`${me.NAME || me.EMAIL} <${me.EMAIL}>`);
      setSprintEmployeePickId(me.ID);
    } else {
      setSprintEmployeePick("");
      setSprintEmployeePickId(null);
    }
    onCreateSprintOpen();
  };

  const onSprintEmployeePickChange = (val) => {
    setSprintEmployeePick(val);
    const found = employees.find((e) => {
      const label = `${e.name} <${e.email}>`;
      return label === val || e.email === val || e.name === val;
    });
    setSprintEmployeePickId(found ? found.id : null);
  };

  const [employees, setEmployees] = useState([]);
  const [employeePick, setEmployeePick] = useState("");
  const [employeePickId, setEmployeePickId] = useState(null);

  const [dbCategories, setDbCategories] = useState([]);

  const loadDbCategories = async () => {
    try {
      const res = await api.get("/calendar/categories");
      setDbCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  };

  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);

  const [deletingId, setDeletingId] = useState(null);
  const deletingRef = useRef(false);

  // eslint-disable-next-line no-unused-vars
  const [highlightId, setHighlightId] = useState(null);

  const { isOpen: detailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editLoading, setEditLoading] = useState(false);



  const [modalEmployee, setModalEmployee] = useState(null);

  const effectiveModalEmployee = useMemo(() => {
    if (!modalEmployee) return null;

    const modalId = modalEmployee.id ?? modalEmployee.ID;
    const modalEmail = String(modalEmployee.email || modalEmployee.EMAIL || "").toLowerCase();
    const found = employees.find((emp) => {
      const empId = emp.id ?? emp.ID;
      const empEmail = String(emp.email || emp.EMAIL || "").toLowerCase();
      return (
        (modalId != null && empId != null && String(empId) === String(modalId)) ||
        (modalEmail && empEmail && empEmail === modalEmail)
      );
    });

    return found ? { ...modalEmployee, ...found } : modalEmployee;
  }, [modalEmployee, employees]);
  const [leavesForStats, setLeavesForStats] = useState([]);
  const [filterEmployeeIds, setFilterEmployeeIds] = useState([]); // [] = Show All
  const [filterSearch, setFilterSearch] = useState("");

  // Holiday popover state
  const [holidayPopover, setHolidayPopover] = useState(null); // { title, date, x, y }
  const holidayClickedRef = useRef(0); // timestamp of last holiday click
  const filterInitRef = useRef(false); // Track if filter was initialized to "Me"

  // =========================================================
  // ✅ Seleção verde própria (SEM FALHAR ao passar por cima de evento)
  // =========================================================
  const clearDragRange = useCallback(() => {
    const root = stageRef.current;
    if (!root) return;
    root
      .querySelectorAll(".fc-daygrid-day.nogle-range-basic")
      .forEach((el) => el.classList.remove("nogle-range-basic"));
  }, []);

  const paintDragRange = useCallback(
    (aISO, bISO) => {
      const root = stageRef.current;
      if (!root) return;

      clearDragRange();

      const a = moment(aISO, ISO_FMT, true);
      const b = moment(bISO, ISO_FMT, true);
      if (!a.isValid() || !b.isValid()) return;

      const start = moment.min(a, b);
      const end = moment.max(a, b);

      let cur = moment(start);
      while (cur.isSameOrBefore(end, "day")) {
        const iso = cur.format(ISO_FMT);
        const cell = root.querySelector(`.fc-daygrid-day[data-date="${iso}"]`);
        if (cell) cell.classList.add("nogle-range-basic");
        cur.add(1, "day");
      }
    },
    [clearDragRange]
  );

  useEffect(() => {
    const root = stageRef.current;
    if (!root) return;

    const drag = { active: false, start: null, last: null };

    const getDayCellFromPoint = (x, y) => {
      const list = typeof document.elementsFromPoint === "function" ? document.elementsFromPoint(x, y) : [];
      for (const el of list) {
        const cell = el?.closest?.(".fc-daygrid-day[data-date]");
        if (cell) return cell;
      }
      return null;
    };

    const onDown = (e) => {
      if (e.button !== 0) return;
      if (e.target?.closest?.(".fc-event") || e.target?.closest?.("button")) return;

      const cell = getDayCellFromPoint(e.clientX, e.clientY) || e.target?.closest?.(".fc-daygrid-day[data-date]");
      if (!cell) return;

      drag.active = true;
      drag.start = cell.getAttribute("data-date");
      drag.last = drag.start;

      paintDragRange(drag.start, drag.start);
    };

    const onMove = (e) => {
      if (!drag.active) return;

      const cell = getDayCellFromPoint(e.clientX, e.clientY);
      if (!cell) return;

      const cur = cell.getAttribute("data-date");
      if (!cur || cur === drag.last) return;

      drag.last = cur;
      paintDragRange(drag.start, cur);
    };

    const stop = () => {
      if (!drag.active) return;
      drag.active = false;
      drag.start = null;
      drag.last = null;
      clearDragRange();
    };

    root.addEventListener("mousedown", onDown);
    root.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("blur", stop);

    return () => {
      root.removeEventListener("mousedown", onDown);
      root.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("blur", stop);
    };
  }, [paintDragRange, clearDragRange]);

  // ---------- load me ----------
  const loadMe = useCallback(async () => {
    try {
      const r = await api.get("/calendar/me");
      setMe(r.data || null);
    } catch (err) {
      if (err?.response?.status === 403) {
        await signOut();
        navigate("/login", { replace: true });
        return;
      }
      console.error("loadMe failed", err);
      setMe(null);
    }
  }, [signOut, navigate]);

  // ---------- employees ----------
  const loadEmployees = useCallback(async () => {
    // if (!isAdmin) return; // ✅ Allow everyone to see list for filtering
    try {
      const r = await api.get("/calendar/employees");
      setEmployees(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      if (err?.response?.status === 403) return;
      console.error("loadEmployees failed", err);
      setEmployees([]);
    }
  }, []);

  // ---------- leaves for modal stats ----------
  const loadLeavesForStats = useCallback(
    async (targetEmployeeId) => {
      try {
        let url = "/calendar/events";

        if (isAdmin && targetEmployeeId && String(targetEmployeeId) !== String(me?.ID || "")) {
          url = `/calendar/events/employee/${encodeURIComponent(String(targetEmployeeId))}`;
        }

        const r = await api.get(url);
        const arr = Array.isArray(r.data) ? r.data : [];

        // ✅ Normalize to INCLUSIVE end for stats
        const normalized = arr
          .map((ev) => {
            const rawStart = toISO(ev?.start || ev?.StartDate || ev?.START_DATE || ev?.rangeStart);
            const rawEnd = toISO(ev?.end || ev?.EndDate || ev?.END_DATE || ev?.rangeEnd) || rawStart;
            if (!rawStart) return null;

            const norm = normalizeBackendEndToInclusive(rawStart, rawEnd);
            if (!norm.startISO || !norm.endISO) return null;

            return {
              ...ev,
              start: norm.startISO,
              end: norm.endISO,
              rangeStart: norm.startISO,
              rangeEnd: norm.endISO,
            };
          })
          .filter(Boolean);

        setLeavesForStats(normalized);
      } catch (err) {
        if (err?.response?.status === 403) {
          setLeavesForStats([]);
          return;
        }
        console.error("loadLeavesForStats failed", err);
        setLeavesForStats([]);
      }
    },
    [isAdmin, me]
  );

  // =========================================================
  // ✅ Eventos em “linha”: segmenta por dia e junta no CSS
  // =========================================================
  const buildSegments = useCallback(
    (arr) => {
      const segs = [];

      (arr || []).forEach((ev) => {
        const backendId = ev.id ?? ev.ID ?? ev.Id ?? ev.uuid ?? ev.UUID ?? null;
        if (!backendId) return;

        const rawStart = toISO(ev.start || ev.StartDate || ev.START_DATE || ev.rangeStart);
        const rawEnd = toISO(ev.end || ev.EndDate || ev.END_DATE || ev.rangeEnd) || rawStart;
        if (!rawStart) return;

        // ✅ Backend END is EXCLUSIVE -> convert to INCLUSIVE for UI
        const norm = normalizeBackendEndToInclusive(rawStart, rawEnd);
        const startISO = norm.startISO;
        const endISO = norm.endISO;

        if (!startISO || !endISO) return;

        // ✅ Now always inclusive count
        let dayCount = inclusiveDaysCount(startISO, endISO);
        if (!Number.isFinite(dayCount) || dayCount <= 0) dayCount = 1;

        const userNameRaw =
          ev.employeeName ||
          ev.employeeEmail ||
          ev.userName ||
          ev.user ||
          me?.NAME ||
          me?.EMAIL ||
          user?.email ||
          "";



        // ✅ Category Color
        const category = ev.category || ev.Category || "Férias";
        const catColor = ev.categoryColor || ev.CategoryColor || ev.raw?.categoryColor || ev.raw?.CategoryColor || getCategoryColor(category);

        for (let i = 0; i < dayCount; i++) {
          const day = moment(startISO, ISO_FMT, true).add(i, "days").format(ISO_FMT);

          const showLabel = i === 0;
          const showDelete = i === dayCount - 1;

          segs.push({
            id: `${String(backendId)}:${day}`,
            title: ev.title || ev.comment || ev.Comment || "OFF",
            start: day,
            end: moment(day, ISO_FMT, true).add(1, "day").format(ISO_FMT),
            allDay: true,
            extendedProps: {
              backendId: String(backendId),
              employeeId: ev.employeeId || ev.EMPLOYEE_ID || ev.employeeID || null,
              userName: stripEmailDomain(userNameRaw),
              employeeName: ev.employeeName || null,
              employeeEmail: ev.employeeEmail || null,
              raw: ev,

              rangeStart: startISO,
              rangeEnd: endISO, // ✅ inclusive for UI
              category: category,

              segIndex: i,
              segCount: dayCount,
              segDate: day,

              showLabel,
              showDelete,
              // Category based color OR Employee Color (Priority)
              color: (() => {
                const eID = String(ev.employeeId || ev.EMPLOYEE_ID || ev.employeeID || "");
                // 1. Try finding in employees list
                const found = employees.find(x => String(x.id) === eID);
                const empColor = found?.color_AG_EMPLOYEES || found?.COLOR_AG_EMPLOYEES;
                if (empColor) return empColor;

                // 2. Try checking if it's "me"
                if (me && String(me.ID) === eID) {
                  const meColor = me.color_AG_EMPLOYEES || me.COLOR_AG_EMPLOYEES;
                  if (meColor) return meColor;
                }

                // 3. Fallback to category
                return catColor;
              })(),
            },
          });
        }
      });

      return segs;
    },
    [me, user, employees]
  );

  // =========================================================
  // ✅ Anti-race: ID incremental para garantir ordem
  // =========================================================
  const requestIdRef = useRef(0);

  const loadEventsRange = useCallback(
    async (rangeStartISO, rangeEndISO) => {
      if (!me) return;

      const rid = requestIdRef.current + 1;
      requestIdRef.current = rid;

      setEventsLoading(true);

      try {
        let url = "/calendar/events";
        if (isAdmin) {
          const s = toISO(rangeStartISO);
          const e = toISO(rangeEndISO);
          url = `/calendar/events/all?start=${encodeURIComponent(String(s))}&end=${encodeURIComponent(String(e))}`;
        } else {
          // Try fetching ALL for non-admins too (whitelist check on backend?)
          // If 403, we fallback to normal events below
          const s = toISO(rangeStartISO);
          const e = toISO(rangeEndISO);
          url = `/calendar/events/all?start=${encodeURIComponent(String(s))}&end=${encodeURIComponent(String(e))}`;
        }

        let res;
        try {
          res = await api.get(url);
        } catch (initialErr) {
          // If /all is forbidden for non-admin, fallback to normal /events
          if (initialErr?.response?.status === 403 && !isAdmin) {
            url = "/calendar/events";
            res = await api.get(url);
          } else {
            throw initialErr; // rethrow other errors or if admin
          }
        }

        if (rid !== requestIdRef.current) return;

        const arr = Array.isArray(res.data) ? res.data : [];
        const segs = buildSegments(arr);

        const allEvents = segs;

        setEvents(allEvents);
      } catch (err) {
        if (rid !== requestIdRef.current) return;

        if (err?.response?.status === 403) {
          await signOut();
          navigate("/login", { replace: true });
          return;
        }

        console.error("loadEventsRange failed", err);
        setEvents([]);

        toast({
          status: "error",
          title: "Erro ao carregar eventos",
          description: err?.response?.data?.message || err?.message || "Veja console",
        });
      } finally {
        if (rid === requestIdRef.current) {
          setEventsLoading(false);
        }
      }
    },
    [isAdmin, buildSegments, toast, signOut, navigate, me]
  );

  const refetchCurrentRange = useCallback(async () => {
    const apiRef = calendarApi();
    if (!apiRef?.view) return;
    const v = apiRef.view;
    await loadEventsRange(toISO(v.activeStart), toISO(v.activeEnd));
  }, [loadEventsRange]);

  // ---------- init ----------
  useEffect(() => {
    if (!user) return;
    void loadMe();
    void loadDbCategories();
  }, [user, loadMe]);

  useEffect(() => {
    if (!me) return;

    // Initialize filter (ONCE)
    if (!filterInitRef.current && me.ID) {
      if (isAdmin) {
        setFilterEmployeeIds([]); // Admin sees all by default
      } else {
        setFilterEmployeeIds([String(me.ID)]); // User sees Self by default
      }
      filterInitRef.current = true;
    }

    (async () => {
      await loadEmployees(); // Fetch for everyone (safe)

      const apiRef = calendarApi();
      if (apiRef) setViewTitle(formatViewTitle(apiRef.view?.title || ""));

      setModalEmployee(me);
      await loadLeavesForStats(me?.ID);

      await refetchCurrentRange();

      // ✅ Check for date param to navigate
      const dateParam = searchParams.get("date");
      if (dateParam) {
        const mDate = moment(dateParam, ISO_FMT, true);
        if (mDate.isValid() && apiRef) {
          apiRef.gotoDate(dateParam);
          syncTitle();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // ---------- navigation ----------
  const syncTitle = () => {
    const apiRef = calendarApi();
    if (!apiRef) return;
    setTimeout(() => setViewTitle(formatViewTitle(apiRef.view?.title || "")), 0);
  };

  const prev = () => {
    const apiRef = calendarApi();
    if (!apiRef) return;
    apiRef.prev();
    syncTitle();
  };

  const next = () => {
    const apiRef = calendarApi();
    if (!apiRef) return;
    apiRef.next();
    syncTitle();
  };

  const goToday = () => {
    const apiRef = calendarApi();
    if (!apiRef) return;
    apiRef.today();
    syncTitle();
  };

  const changeView = (name) => {
    const apiRef = calendarApi();
    if (!apiRef) return;
    apiRef.changeView(name);
    syncTitle();
  };

  const handleDatesSet = (dateInfo) => {
    setViewTitle(formatViewTitle(dateInfo.view?.title || ""));

    const s = toISO(dateInfo.startStr);
    const e = toISO(dateInfo.endStr);
    void loadEventsRange(s, e);
  };

  // ---------- clicks ----------
  const handleEventClick = (clickInfo) => {
    setSelectedEvent(clickInfo?.event || null);
    onDetailsOpen();
  };

  // ---------- create ----------
  const resetModal = () => {
    setStartDate("");
    setEndDate("");
    setCategory("Férias");
    setComment("");
    setModalError("");
    setEmployeePick("");
    setEmployeePickId(null);

    setModalEmployee(me);
    void loadLeavesForStats(me?.ID);
  };

  const openCreateModalFor = (startISO, endISO) => {
    setStartDate(startISO);
    setEndDate(endISO);
    setCategory("Férias");
    setComment("");
    setModalError("");

    if (isAdmin && employeePickId) {
      const emp = employees.find((e) => String(e.id) === String(employeePickId)) || null;
      setModalEmployee(emp || me);
      void loadLeavesForStats(emp?.id || me?.ID);
    } else {
      setModalEmployee(me);
      void loadLeavesForStats(me?.ID);
    }

    onCreateOpen();
  };

  const handleDateClick = (arg) => {
    if (creating) return;
    // Block if a holiday overlay was just clicked (within 300ms)
    if (Date.now() - holidayClickedRef.current < 300) return;
    openCreateModalFor(arg.dateStr, arg.dateStr);
  };

  const handleSelect = (selectInfo) => {
    if (creating) return;
    // Block if a holiday overlay was just clicked (within 300ms)
    if (Date.now() - holidayClickedRef.current < 300) return;

    const startISO = toISO(selectInfo.startStr) || toISO(selectInfo.start);

    // FullCalendar selection gives END EXCLUSIVE for allDay.
    // Convert to INCLUSIVE for modal/backend by subtracting 1 day.
    let endISO = toISO(selectInfo.endStr) || toISO(selectInfo.end) || startISO;

    if (selectInfo?.allDay && endISO) {
      endISO = moment(endISO, ISO_FMT, true).subtract(1, "day").format(ISO_FMT);
    }

    if (!endISO) endISO = startISO;

    openCreateModalFor(startISO, endISO);

    const apiRef = calendarApi();
    if (apiRef) apiRef.unselect();
  };

  const onEmployeePickChange = (val) => {
    setEmployeePick(val);

    const found = employees.find((e) => {
      const label = `${e.name} <${e.email}>`;
      return label === val || e.email === val || e.name === val;
    });

    const id = found ? found.id : null;
    setEmployeePickId(id);

    if (isAdmin && id) {
      setModalEmployee(found);
      void loadLeavesForStats(id);
    } else {
      setModalEmployee(me);
      void loadLeavesForStats(me?.ID);
    }
  };

  const handleCreate = async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);

    try {
      setModalError("");

      const s = moment(startDate, ISO_FMT, true);
      const e = moment(endDate, ISO_FMT, true);
      if (!s.isValid() || !e.isValid()) return setModalError("Datas inválidas.");

      const today = moment().startOf("day");
      if (s.isBefore(today)) return setModalError("Data início não pode ser anterior a hoje.");
      if (e.isBefore(s)) return setModalError("Data fim não pode ser anterior à data início.");

      // ✅ Backend expects INCLUSIVE from modal
      const payload = {
        start: s.format(ISO_FMT),
        end: e.format(ISO_FMT),
        category,
        comment: sanitizeComment(comment) || null,
      };

      if (isAdmin && employeePickId) payload.employeeId = employeePickId;

      // ✅ Optimistic UI:
      // BuildSegments expects backend END EXCLUSIVE, so we send end+1 here ONLY for optimistic object.
      const tempId = "temp-" + Date.now();
      const optimisticEvt = {
        id: tempId,
        start: payload.start,
        end: moment(payload.end, ISO_FMT, true).add(1, "day").format(ISO_FMT), // exclusive for builder
        category: payload.category,
        comment: payload.comment,
        employeeId: payload.employeeId || me?.ID,
        userName: isAdmin && employeePickId ? employeePick : me?.NAME || me?.EMAIL,
      };

      const newSegs = buildSegments([optimisticEvt]);
      setEvents((prev) => [...(prev || []), ...newSegs]);

      await api.post("/calendar/events", payload);
      await loadMe();

      toast({ status: "success", title: "Período criado com sucesso" });

      await refetchCurrentRange();

      if (isAdmin && employeePickId) {
        const emp = employees.find((x) => String(x.id) === String(employeePickId)) || null;
        setModalEmployee(emp || me);
        await loadLeavesForStats(emp?.id || me?.ID);
      } else {
        setModalEmployee(me);
        await loadLeavesForStats(me?.ID);
      }

      onCreateClose();
      resetModal();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Erro ao criar";

      if (status && status < 500) setModalError(msg);
      else toast({ status: "error", title: "Erro ao criar", description: msg });

      await refetchCurrentRange();
    } finally {
      setCreating(false);
      creatingRef.current = false;
    }
  };

  const handleCreateSprint = async () => {
    setSprintModalError("");

    const s = moment(sprintStartDate, ISO_FMT, true);
    const e = moment(sprintEndDate, ISO_FMT, true);
    if (!s.isValid() || !e.isValid()) return setSprintModalError("Datas inválidas.");
    if (e.isBefore(s)) return setSprintModalError("Data fim não pode ser anterior à data início.");

    const today = moment().startOf("day");
    if (s.isBefore(today)) return setSprintModalError("Data início não pode ser anterior a hoje.");
    if (e.isAfter(s)) return setSprintModalError("A duração da Sprint deve ser de no máximo 1 dia (mesma data de início e fim).");

    const sprintNumVal = Number(sprintNumber) || 74;

    const warnings = [];
    if (sprintNumVal < latestSprintNum) {
      warnings.push(`O número da Sprint (${sprintNumVal}) é anterior à Sprint mais recente cadastrada (${latestSprintNum}).`);
    }
    if (hasSprintIn15Days) {
      warnings.push(`Já existe um evento de Sprint criado no intervalo de 15 dias.`);
    }

    if (warnings.length > 0) {
      setSprintWarnMessages(warnings);
      onSprintWarnOpen();
      return;
    }

    await submitSprint();
  };

  const submitSprint = async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);

    try {
      const s = moment(sprintStartDate, ISO_FMT, true);
      const e = moment(sprintEndDate, ISO_FMT, true);
      const sprintNumVal = Number(sprintNumber) || 74;

      const payload = {
        start: s.format(ISO_FMT),
        end: e.format(ISO_FMT),
        category: "Sprint",
        comment: `Sprint ${sprintNumVal}` + (sprintComment.trim() ? `: ${sprintComment.trim()}` : ""),
      };

      if (isAdmin && sprintEmployeePickId) {
        payload.employeeId = sprintEmployeePickId;
      }

      // Optimistic UI:
      const tempId = "temp-sprint-" + Date.now();
      const optimisticEvt = {
        id: tempId,
        start: payload.start,
        end: moment(payload.end, ISO_FMT, true).add(1, "day").format(ISO_FMT),
        category: payload.category,
        comment: payload.comment,
        employeeId: payload.employeeId || me?.ID,
        userName: isAdmin && sprintEmployeePickId ? sprintEmployeePick : me?.NAME || me?.EMAIL,
      };

      const newSegs = buildSegments([optimisticEvt]);
      setEvents((prev) => [...(prev || []), ...newSegs]);

      await api.post("/calendar/events", payload);
      await loadMe();

      toast({ status: "success", title: "Sprint criada com sucesso" });

      await refetchCurrentRange();
      onSprintWarnClose();
      onCreateSprintClose();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Erro ao criar sprint";

      if (status && status < 500) setSprintModalError(msg);
      else toast({ status: "error", title: "Erro ao criar sprint", description: msg });

      await refetchCurrentRange();
    } finally {
      setCreating(false);
      creatingRef.current = false;
    }
  };

  // ---------- delete ----------
  const isPastEvent = useCallback((ev) => {
    const endInclusive = ev?.extendedProps?.rangeEnd || toISO(ev.start);
    const endM = moment(endInclusive, ISO_FMT, true).startOf("day");
    const todayM = moment().startOf("day");
    return endM.isBefore(todayM, "day");
  }, []);

  const canDeleteEvent = useCallback(
    (ev) => {
      if (isAdmin) return true;
      return !isPastEvent(ev);
    },
    [isAdmin, isPastEvent]
  );

  const handleDeleteEvent = async (fcEvent) => {
    const backendId = fcEvent?.extendedProps?.backendId;
    if (!backendId) {
      toast({ status: "error", title: "Erro ao excluir", description: "Não foi possível determinar o ID do evento." });
      return;
    }

    if (!canDeleteEvent(fcEvent)) {
      toast({
        status: "warning",
        title: "Bloqueado",
        description: "Períodos passados não podem ser excluídos (exceto admin).",
      });
      return;
    }

    if (deletingRef.current) return;
    if (deletingId === String(backendId)) return;

    deletingRef.current = true;

    setEvents((prev) => (prev || []).filter((x) => String(x?.extendedProps?.backendId || "") !== String(backendId)));

    try {
      setDeletingId(String(backendId));
      await api.delete(`/calendar/events/${encodeURIComponent(String(backendId))}`);

      await loadMe();

      if (isAdmin && employeePickId) {
        const emp = employees.find((x) => String(x.id) === String(employeePickId)) || null;
        setModalEmployee(emp || me);
        await loadLeavesForStats(emp?.id || me?.ID);
      } else {
        setModalEmployee(me);
        await loadLeavesForStats(me?.ID);
      }

      toast({ status: "success", title: "Período excluído" });

      if (selectedEvent?.extendedProps?.backendId && String(selectedEvent.extendedProps.backendId) === String(backendId)) {
        onDetailsClose();
        setSelectedEvent(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Veja console";
      toast({ status: "error", title: "Erro ao excluir", description: msg });

      await refetchCurrentRange();
    } finally {
      setDeletingId(null);
      deletingRef.current = false;
    }
  };

  // ---------- edit ----------
  const handleStartEdit = () => {
    if (!selectedEvent) return;
    setIsEditing(true);

    const props = selectedEvent.extendedProps || {};

    const s = props.rangeStart || toISO(selectedEvent.startStr || selectedEvent.start);
    const e = props.rangeEnd || s; // ✅ inclusive (normalized)

    setEditStart(s);
    setEditEnd(e);
    setEditCategory(props.category || props.raw?.category || "Férias");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditStart("");
    setEditEnd("");
    setEditCategory("");
  };

  const handleSaveEdit = async () => {
    if (editLoading) return;
    setEditLoading(true);

    try {
      const backendId = selectedEvent?.extendedProps?.backendId;
      if (!backendId) throw new Error("ID do evento não encontrado.");

      const s = moment(editStart, ISO_FMT, true);
      const e = moment(editEnd, ISO_FMT, true);

      if (!s.isValid() || !e.isValid()) {
        toast({ status: "warning", title: "Datas inválidas." });
        setEditLoading(false);
        return;
      }
      if (e.isBefore(s)) {
        toast({ status: "warning", title: "Data fim deve ser igual ou maior que início." });
        setEditLoading(false);
        return;
      }

      // ✅ Send inclusive to backend
      const sendStart = s.format(ISO_FMT);
      const sendEnd = e.format(ISO_FMT);

      const existingComment =
        selectedEvent?.extendedProps?.raw?.comment || selectedEvent?.extendedProps?.raw?.Comment || "";

      await api.put(`/calendar/events/${backendId}`, {
        start: sendStart,
        end: sendEnd,
        category: editCategory,
        comment: existingComment,
      });

      toast({ status: "success", title: "Período atualizado!" });

      setIsEditing(false);
      onDetailsClose();
      setSelectedEvent(null);

      await refetchCurrentRange();
      await loadMe();

      if (isAdmin && employeePickId) {
        await loadLeavesForStats(employeePickId);
      } else {
        await loadLeavesForStats(me?.ID);
      }
    } catch (err) {
      console.error(err);
      toast({
        status: "error",
        title: "Erro ao salvar",
        description: err?.response?.data?.message || err?.message || "Tente novamente.",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // ---------- render ----------
  const dayCellClassNames = (arg) => {
    try {
      const iso = arg.date ? moment(arg.date).format(ISO_FMT) : null;
      const classes = [];
      if (iso === todayISO) classes.push("fc-day-today-custom");
      if (getHolidayByDate(iso)) classes.push("fc-day-holiday");
      return classes;
    } catch {
      return [];
    }
  };

  const eventClassNames = (arg) => {

    const classes = ["nogle-off-event"];

    const showLabel = !!arg?.event?.extendedProps?.showLabel;
    const showDelete = !!arg?.event?.extendedProps?.showDelete;

    if (showLabel && showDelete) classes.push("nogle-seg-single");
    else if (showLabel) classes.push("nogle-seg-start");
    else if (showDelete) classes.push("nogle-seg-end");
    else classes.push("nogle-seg-mid");

    const backendId = String(arg?.event?.extendedProps?.backendId || "");
    if (highlightId && backendId && backendId === String(highlightId)) classes.push("event-highlight");

    return classes;
  };

  const eventContent = (arg) => {
    const isSprint = arg.event.extendedProps?.category === "Sprint";
    let userName = arg.event.extendedProps?.userName || "";
    let userBubble = userName ? userName.charAt(0).toUpperCase() : "";

    if (isSprint) {
      const raw = arg.event.extendedProps?.raw;
      const sprintNum = getSprintNumber(raw?.comment || raw?.COMMENT, arg.event.title);
      userName = sprintNum != null ? `Virada de Sprint ${sprintNum}` : "Virada de Sprint";
      userBubble = "S";
    }

    const backendId = arg.event.extendedProps?.backendId || null;
    const userColor = arg.event.extendedProps?.color || null;

    // ✅ Inject custom color variable if present
    const style = userColor ? { "--user-event-color": userColor } : {};

    const showLabel = !!arg.event.extendedProps?.showLabel;
    const showDelete = !!arg.event.extendedProps?.showDelete;

    const deleting = deletingId && backendId && String(backendId) === String(deletingId);
    const allowed = canDeleteEvent(arg.event);

    const DeleteButton = () => (
      <button
        className="fc-event-delete-button"
        disabled={!!deleting || !allowed}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDeleteEvent(arg.event);
        }}
        aria-label="Excluir"
        title={!allowed ? "Período passado (bloqueado)" : deleting ? "Excluindo..." : "Excluir"}
        style={{
          opacity: !allowed || deleting ? 0.5 : 1,
          cursor: !allowed || deleting ? "not-allowed" : "pointer",
        }}
      >
        <Icon as={AiOutlineClose} />
      </button>
    );

    if (highlightId && backendId && backendId === String(highlightId)) {
      return (
        <div className="fc-event-custom-card event-highlight" title={arg.event.title} style={style}>
          <div className="nogle-event-left">
            <div className="fc-event-user-bubble">{userBubble}</div>
            <div className="nogle-event-name">{userName}</div>
          </div>
        </div>
      );
    }

    if (!showLabel && !showDelete) {
      return <div className="fc-event-custom-card nogle-seg-fill" title={arg.event.title} style={style} />;
    }

    if (showLabel && !showDelete) {
      return (
        <div className="fc-event-custom-card nogle-seg-first" title={arg.event.title} style={style}>
          <div className="nogle-event-left">
            <div className="fc-event-user-bubble">{userBubble}</div>
            <div className="nogle-event-name">{userName}</div>
          </div>
        </div>
      );
    }

    if (!showLabel && showDelete) {
      return (
        <div className="fc-event-custom-card nogle-seg-last" title={arg.event.title} style={style}>
          <DeleteButton />
        </div>
      );
    }

    return (
      <div className="fc-event-custom-card" title={arg.event.title} style={style}>
        <div className="nogle-event-left">
          <div className="fc-event-user-bubble">{userBubble}</div>
          <div className="nogle-event-name">{userName}</div>
        </div>
        <DeleteButton />
      </div>
    );
  };

  // ---------- detalhes ----------
  const detailsStartISO = useMemo(() => {
    if (!selectedEvent) return null;
    return selectedEvent?.extendedProps?.rangeStart || toISO(selectedEvent.startStr || selectedEvent.start);
  }, [selectedEvent]);

  const detailsEndISO = useMemo(() => {
    if (!selectedEvent) return null;
    return selectedEvent?.extendedProps?.rangeEnd || detailsStartISO;
  }, [selectedEvent, detailsStartISO]);

  const selectedCanDelete = useMemo(() => {
    if (!selectedEvent) return false;
    return canDeleteEvent(selectedEvent);
  }, [selectedEvent, canDeleteEvent]);

  const selectedBackendId = useMemo(() => {
    return selectedEvent?.extendedProps?.backendId ? String(selectedEvent.extendedProps.backendId) : null;
  }, [selectedEvent]);

  const selectedDeleting = useMemo(() => {
    return deletingId && selectedBackendId && String(deletingId) === String(selectedBackendId);
  }, [deletingId, selectedBackendId]);

  // =========================================================
  // ✅ SUMMARY (por admissão)
  // =========================================================
  const targetEndISOForSummary = useMemo(() => {
    return toISO(endDate) || toISO(startDate) || todayISO;
  }, [endDate, startDate, todayISO]);

  const targetAdmissionISO = useMemo(() => {
    const raw = effectiveModalEmployee?.admissionDate || effectiveModalEmployee?.ADMISSION_DATE || effectiveModalEmployee?.admission_date || null;
    return toISO(raw);
  }, [effectiveModalEmployee]);

  const cycle = useMemo(() => {
    return getAdmissionCycleWindow(targetAdmissionISO, targetEndISOForSummary);
  }, [targetAdmissionISO, targetEndISOForSummary]);

  const usedDaysInCycle = useMemo(() => {
    if (!cycle?.cycleStartISO || !cycle?.cycleEndISO) return 0;

    let used = 0;
    (leavesForStats || []).forEach((ev) => {
      const rs = toISO(ev?.start || ev?.START_DATE || ev?.rangeStart);
      const re = toISO(ev?.end || ev?.END_DATE || ev?.rangeEnd) || rs;
      if (!rs || !re) return;

      // ✅ Only count "Férias" (robust check)
      const rawCat = ev.category || ev.Category || "Férias";
      const cat = String(rawCat).toLowerCase().trim();
      // Check for "férias" or "ferias" (accent insensitive compatible)
      if (!cat.includes("férias") && !cat.includes("ferias")) return;

      used += overlapDaysInclusive(rs, re, cycle.cycleStartISO, cycle.cycleEndISO);
    });

    return used;
  }, [leavesForStats, cycle]);

  const selectedDays = useMemo(() => {
    const s = toISO(startDate);
    const e = toISO(endDate);
    if (!s || !e) return 0;
    return inclusiveDaysCount(s, e);
  }, [startDate, endDate]);

  // Apenas Férias consome o ciclo: outras categorias não entram na projeção
  const projectedTotal = useMemo(
    () => usedDaysInCycle + (isFeriasCategory(category) ? selectedDays || 0 : 0),
    [usedDaysInCycle, selectedDays, category]
  );
  const maxDaysForModal = useMemo(() => getMaxDaysForEmployee(effectiveModalEmployee), [effectiveModalEmployee]);
  const overLimit = useMemo(() => projectedTotal > maxDaysForModal, [projectedTotal, maxDaysForModal]);


  // ✅ Filter Events
  const filteredEvents = useMemo(() => {
    if (filterEmployeeIds.length === 0) return events;
    return events.filter((e) => {
      if (e.extendedProps?.category === "Sprint") return true;
      const uid = String(e.extendedProps?.employeeId || "");
      return filterEmployeeIds.includes(uid);
    });
  }, [events, filterEmployeeIds]);




  const latestSprintNum = useMemo(() => {
    let maxNum = 0;
    events.forEach(ev => {
      if (ev.extendedProps?.category === "Sprint") {
        const raw = ev.extendedProps?.raw;
        const num = getSprintNumber(raw?.comment || raw?.COMMENT, ev.title);
        if (num != null && num > maxNum) maxNum = num;
      }
    });
    return maxNum;
  }, [events]);

  const hasSprintIn15Days = useMemo(() => {
    if (!sprintStartDate || !sprintEndDate) return false;
    const newStart = moment(sprintStartDate, ISO_FMT);
    const newEnd = moment(sprintEndDate, ISO_FMT);
    if (!newStart.isValid() || !newEnd.isValid()) return false;

    return events.some(ev => {
      if (ev.extendedProps?.category !== "Sprint") return false;
      const sprintStart = moment(ev.extendedProps.rangeStart, ISO_FMT);
      const sprintEnd = moment(ev.extendedProps.rangeEnd, ISO_FMT);
      if (!sprintStart.isValid() || !sprintEnd.isValid()) return false;

      const diffStart = Math.abs(newStart.diff(sprintStart, 'days'));
      const diffEnd = Math.abs(newEnd.diff(sprintEnd, 'days'));
      return diffStart <= 15 || diffEnd <= 15;
    });
  }, [events, sprintStartDate, sprintEndDate]);

  // Sprint fica fora do dropdown: só é criada pelo fluxo próprio (botão Criar Sprint)
  const categoriesToRender = dbCategories.length > 0 ? dbCategories.filter(c => c.active && !isSprintCategory(c.name || c.NAME)) : [
    { id: 1, name: "Férias", color: "#02ea75" },
    { id: 2, name: "Evento", color: "#805ad5" },
    { id: 3, name: "DayOff", color: "#3182ce" },
    { id: 4, name: "Folga", color: "#ecc94b" },
  ];

  return (
    <Box>
      <Box className="calendar-wrapper" w="100%" mx="auto" px={{ base: 4, md: 0 }} mb={4}>
        <Flex align="center" justify="space-between" mb={2} wrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Button className="calendar-btn" onClick={prev} aria-label="Anterior">
              <Icon as={AiOutlineLeft} />
            </Button>
            <Button className="calendar-btn" onClick={goToday} aria-label="Hoje">
              Hoje
            </Button>
            <Button className="calendar-btn" onClick={next} aria-label="Próximo">
              <Icon as={AiOutlineRight} />
            </Button>

            <Popover placement="bottom-start" closeOnBlur={true}>
              <PopoverTrigger>
                <Button className="calendar-btn" leftIcon={<Icon as={AiOutlineFilter} boxSize={4} />} gap={1} px={3}>
                  Filtros {filterEmployeeIds.length > 0 && `(${filterEmployeeIds.length})`}
                </Button>
              </PopoverTrigger>
              <PopoverContent bg="surface" borderColor="borderPrimary" color="textPrimary" w="320px" _focus={{ boxShadow: "none" }}>
                <PopoverArrow bg="surface" />
                <PopoverHeader borderColor="borderPrimary" fontWeight="bold" fontSize="sm">
                  Filtrar Funcionários
                </PopoverHeader>
                <PopoverCloseButton />
                <PopoverBody maxH="400px" overflowY="auto" p={0}>
                  <Box p={3} borderBottom="1px solid" borderColor="borderPrimary">
                    <Input
                      placeholder="Buscar..."
                      size="sm"
                      bg="surfaceRaised"
                      border="1px solid"
                      borderColor="borderPrimary"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      mb={2}
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="green"
                      w="100%"
                      onClick={() => setFilterEmployeeIds([])}
                      isDisabled={filterEmployeeIds.length === 0}
                    >
                      Limpar Filtros
                    </Button>
                  </Box>
                  <Stack spacing={0}>
                    {employees
                      .filter(emp => {
                        if (!filterSearch) return true;
                        return emp.name.toLowerCase().includes(filterSearch.toLowerCase());
                      })
                      .map((emp) => {
                        const isChecked = filterEmployeeIds.includes(String(emp.id));
                        return (
                          <Box
                            key={emp.id}
                            px={4}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: "rgba(255,255,255,0.05)" }}
                            onClick={() => {
                              setFilterEmployeeIds(prev => {
                                const sid = String(emp.id);
                                if (prev.includes(sid)) return prev.filter(x => x !== sid);
                                return [...prev, sid];
                              });
                            }}
                            display="flex"
                            alignItems="center"
                            gap={3}
                          >
                            <Checkbox
                              isChecked={isChecked}
                              colorScheme="green"
                              borderColor="borderPrimary"
                              pointerEvents="none"
                            />
                            <Avatar size="xs" name={emp.name} src={emp.avatar || emp.url} />
                            <Text fontSize="sm" noOfLines={1}>{emp.name}</Text>
                          </Box>
                        );
                      })}
                    {employees.length === 0 && (
                      <Box p={4} textAlign="center" color="textMuted" fontSize="sm">
                        Nenhum funcionário encontrado.
                      </Box>
                    )}
                  </Stack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
            {/* )} */}


          </HStack>

          <Box textAlign="center" flex="1">
            <Text display="inline-block" fontSize={{ base: "lg", md: "xl" }} fontWeight={900} color="textPrimary" mx="auto">
              {viewTitle || ""}
            </Text>
          </Box>

          <HStack spacing={3}>
            <Button className="calendar-btn" onClick={() => changeView("dayGridMonth")}>
              Mês
            </Button>
            <Button className="calendar-btn" onClick={() => changeView("timeGridWeek")}>
              Semana
            </Button>
            <Button className="calendar-btn" onClick={() => changeView("timeGridDay")}>
              Dia
            </Button>

            {isAdmin && (
              <Button
                colorScheme="purple"
                isDisabled={creating}
                onClick={() => {
                  if (creating) return;
                  const now = moment().format(ISO_FMT);
                  openCreateSprintModalFor(now, now);
                }}
              >
                Criar Sprint
              </Button>
            )}
            <Button
              colorScheme="green"
              isDisabled={creating}
              onClick={() => {
                if (creating) return;
                const now = moment().format(ISO_FMT);
                openCreateModalFor(now, now);
              }}
            >
              Criar
            </Button>
          </HStack>
        </Flex>
      </Box>

      <Box className="calendar-wrapper" w="100%" mx="auto" px={{ base: 4, md: 0 }}>
        <Box
          ref={stageRef}
          className="calendar-stage my-fullcalendar"
          borderRadius="2xl"
          bg="surface"
          border="1px solid"
          borderColor="borderPrimary"
          p={6}
          position="relative"
        >
          {eventsLoading ? (
            <Box
              position="absolute"
              inset={0}
              zIndex={5}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="overlayBg"
              borderRadius="2xl"
              pointerEvents="none"
            >
              <HStack spacing={3} bg="surface" border="1px solid" borderColor="borderPrimary" px={4} py={2} borderRadius="xl">
                <Spinner size="sm" />
                <Text color="textPrimary" fontWeight={800} fontSize="sm">
                  Carregando eventos...
                </Text>
              </HStack>
            </Box>
          ) : null}

          <FullCalendar
            key="cleanup-holidays-v2"
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
            initialView="dayGridMonth"
            selectable={!creating}
            select={handleSelect}
            dateClick={handleDateClick}
            events={filteredEvents}
            eventClassNames={eventClassNames}
            eventContent={eventContent}
            eventDidMount={(arg) => {
              const color = arg.event.extendedProps?.color;
              if (color) {
                arg.el.style.setProperty("--user-event-color", color);
              }
            }}
            eventClick={handleEventClick}
            height="calc(100vh - 170px)"
            datesSet={handleDatesSet}
            dayCellClassNames={dayCellClassNames}
            headerToolbar={false}
            dayMaxEventRows={999}
            eventDisplay="block"
            eventOrder="extendedProps.rangeStart,start,-duration"
            moreLinkClick="popover"
            locale={ptBrLocale}
            dayCellDidMount={(arg) => {
              const dateStr = arg.date.toISOString().split("T")[0];
              const holiday = getHolidayByDate(dateStr);
              if (!holiday) return;

              // Inject a holiday overlay into the day cell frame
              const frame = arg.el.querySelector(".fc-daygrid-day-frame");
              if (!frame) return;
              if (frame.querySelector(".holiday-overlay")) return;

              const overlay = document.createElement("div");
              overlay.className = "holiday-overlay";
              overlay.innerHTML = `
                <span class="holiday-icon-el"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/></svg></span>
                <span class="holiday-text-el">${holiday.title}</span>
              `;

              overlay.style.cursor = "pointer";
              // Stop FullCalendar's interaction plugin from detecting this click
              // FC uses pointerdown/mousedown, NOT click, for date selection
              overlay.addEventListener("pointerdown", (e) => { e.stopPropagation(); });
              overlay.addEventListener("mousedown", (e) => { e.stopPropagation(); });
              overlay.addEventListener("click", (e) => {
                e.stopPropagation();
                e.preventDefault();
                holidayClickedRef.current = Date.now();
                const rect = overlay.getBoundingClientRect();
                const popY = rect.bottom + 8;
                const popX = rect.left + rect.width / 2;
                setHolidayPopover({
                  title: holiday.title,
                  date: dateStr,
                  x: popX,
                  y: popY > window.innerHeight - 250 ? rect.top - 8 : popY,
                  above: popY > window.innerHeight - 250,
                });
              });
              frame.appendChild(overlay);
            }}
          />
        </Box>
      </Box>

      {/* Holiday Popover */}
      {holidayPopover && (
        <>
          {/* Backdrop to close */}
          <div
            onClick={() => setHolidayPopover(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9998,
              background: "transparent",
            }}
          />
          <div
            style={{
              position: "fixed",
              left: Math.max(8, Math.min(holidayPopover.x - 140, window.innerWidth - 300)),
              top: holidayPopover.above ? undefined : holidayPopover.y,
              bottom: holidayPopover.above ? (window.innerHeight - holidayPopover.y) : undefined,
              width: "280px",
              zIndex: 9999,
              background: "var(--chakra-colors-surface)",
              border: "1px solid var(--chakra-colors-borderPrimary)",
              borderRadius: "16px",
              boxShadow: "0 18px 42px rgba(2,30,0,0.18)",
              padding: "20px",
              overflow: "hidden",
              animation: "holiday-pop-in 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* Header: Badge & Close */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <span style={{
                background: "rgba(2, 234, 117, 0.1)",
                color: "#02EA75",
                fontSize: "10px",
                fontWeight: "700",
                textTransform: "uppercase",
                padding: "6px 12px",
                borderRadius: "20px",
                letterSpacing: "0.5px",
                border: "1px solid rgba(2, 234, 117, 0.15)",
                boxShadow: "0 2px 10px rgba(2, 234, 117, 0.1)"
              }}>
                Feriado Nacional
              </span>

              <div
                onClick={() => setHolidayPopover(null)}
                style={{
                  cursor: "pointer",
                  color: "var(--chakra-colors-textMuted)",
                  padding: "4px",
                  marginRight: "-8px",
                  marginTop: "-8px",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = "var(--chakra-colors-textPrimary)"}
                onMouseLeave={(e) => e.currentTarget.style.color = "var(--chakra-colors-textMuted)"}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </div>
            </div>

            {/* Content */}
            <div>
              <div style={{
                color: "var(--chakra-colors-textPrimary)",
                fontWeight: 700,
                fontSize: "1.25rem",
                lineHeight: 1.2,
                marginBottom: "8px",
                textShadow: "none"
              }}>
                {holidayPopover.title}
              </div>

              <div style={{
                color: "var(--chakra-colors-textSecondary)",
                fontSize: "0.9rem",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 500
              }}>
                <span style={{ opacity: 0.7 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg></span>
                {(() => {
                  const d = new Date(holidayPopover.date + "T12:00:00");
                  return d.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
                })()}
              </div>
            </div>
          </div>
        </>
      )}




      <Modal
        isOpen={createOpen}
        onClose={() => {
          if (!creating) {
            onCreateClose();
            resetModal();
          }
        }}
        isCentered
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent w="100%" maxW={{ base: "96vw", md: "860px" }} borderRadius="2xl" bg="surface" borderColor="borderPrimary" borderWidth="1px">
          <ModalHeader color="textPrimary" borderBottomWidth="1px" borderColor="borderPrimary" mb={4}>
            <Flex align="center" gap={2}>
              <Icon as={AiOutlineCalendar} color="secondary" />
              <Text>Criar período OFF</Text>
            </Flex>
          </ModalHeader>
          <ModalBody>
            {isAdmin && (
              <>
                <Text fontSize="sm" mb={2}>
                  Funcionário (admin)
                </Text>
                <Input
                  variant="outline"
                  borderColor="rgba(2, 234, 117, 0.3)"
                  borderWidth="1px"
                  bg="surface"
                  _hover={{ borderColor: "secondary" }}
                  _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
                  placeholder="Digite para pesquisar (opcional)"
                  value={employeePick}
                  onChange={(e) => onEmployeePickChange(e.target.value)}
                  list="employeeOptions"
                  mb={3}
                  disabled={creating}
                />
                <datalist id="employeeOptions">
                  {employees.map((e) => (
                    <option key={e.id} value={`${e.name} <${e.email}>`} />
                  ))}
                </datalist>
              </>
            )}

            <Text fontSize="sm" mb={2} color="textSecondary">
              Data início
            </Text>
            <InputGroup>
              <Input
                type="date"
                variant="outline"
                borderColor="rgba(2, 234, 117, 0.3)"
                borderWidth="1px"
                bg="surface"
                _hover={{ borderColor: "secondary" }}
                _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                mb={3}
                disabled={creating}
                sx={{
                  "&::-webkit-calendar-picker-indicator": {
                    filter: "invert(52%) sepia(90%) saturate(2000%) hue-rotate(110deg) brightness(100%) contrast(105%)",
                    cursor: "pointer",
                  },
                }}
              />
            </InputGroup>

            <Text fontSize="sm" mb={2} color="textSecondary">
              Data fim
            </Text>
            <InputGroup>
              <Input
                type="date"
                variant="outline"
                borderColor="rgba(2, 234, 117, 0.3)"
                borderWidth="1px"
                bg="surface"
                _hover={{ borderColor: "secondary" }}
                _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                mb={3}
                disabled={creating}
                sx={{
                  "&::-webkit-calendar-picker-indicator": {
                    filter: "invert(52%) sepia(90%) saturate(2000%) hue-rotate(110deg) brightness(100%) contrast(105%)",
                    cursor: "pointer",
                  },
                }}
              />
            </InputGroup>

            <Text fontSize="sm" mb={2} color="textSecondary">
              Categoria
            </Text>
            <Select
              variant="outline"
              bg="surface"
              borderColor="rgba(2, 234, 117, 0.3)"
              _hover={{ borderColor: "secondary" }}
              _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              mb={3}
              disabled={creating}
              sx={{
                "& > option": {
                  background: "var(--chakra-colors-surface)",
                  color: "var(--chakra-colors-textPrimary)",
                },
              }}
            >
              {categoriesToRender.map((c) => (
                <option key={c.id || c.ID} value={c.name || c.NAME} style={{ backgroundColor: "var(--chakra-colors-surface)", color: "var(--chakra-colors-textPrimary)" }}>
                  {c.name || c.NAME}
                </option>
              ))}
            </Select>

            <Text fontSize="sm" mb={2} color="textSecondary">
              Comentário (opcional)
            </Text>
            <Input
              variant="outline"
              borderColor="rgba(2, 234, 117, 0.3)"
              borderWidth="1px"
              bg="surface"
              _hover={{ borderColor: "secondary" }}
              _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
              placeholder="Ex.: férias, viagem, pessoal..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              mb={3}
              disabled={creating}
            />

            <Text fontSize="sm" color="textSecondary">
              Observação: feriados contam como dias normais. Períodos passados não podem ser excluídos (exceto admin).
            </Text>

            <Divider my={4} />

            <Box border="1px solid" borderColor="borderPrimary" borderRadius="lg" p={3} bg="surfaceRaised">
              <Text fontSize="sm" fontWeight={900} mb={2} color="textSecondary">
                Resumo do ciclo
              </Text>

              <HStack justify="space-between" fontSize="sm" color="textSecondary" mt={1}>
                <Text>Dias já usados no ciclo</Text>
                <Text fontWeight={900}>
                  {usedDaysInCycle} / {maxDaysForModal}
                </Text>
              </HStack>

              <HStack justify="space-between" fontSize="sm" color="textSecondary" mt={1}>
                <Text>Total com este período</Text>
                <Text fontWeight={900}>{projectedTotal}</Text>
              </HStack>

              <HStack justify="space-between" fontSize="sm" color="textSecondary" mt={2}>
                <Text>Próxima virada do ciclo</Text>
                <Text fontWeight={900}>{cycle?.nextCycleStartISO ? fmtBR(cycle.nextCycleStartISO) : "—"}</Text>
              </HStack>

              {cycle?.isInitialCyclePending ? (
                <Box mt={3} borderRadius="md" p={2} bg="rgba(59, 130, 246, 0.10)" border="1px solid rgba(59, 130, 246, 0.25)">
                  <Text fontSize="sm" color="info" fontWeight={900}>
                    Ciclo inicial em andamento
                  </Text>
                  <Text fontSize="sm" color="info" opacity={0.95}>
                    Ainda não completou 1 ano desde a contratação.
                    {cycle?.firstAnniversaryISO ? ` Completa em ${fmtBR(cycle.firstAnniversaryISO)}.` : ""}
                  </Text>
                </Box>
              ) : null}

              {overLimit ? (
                <Box mt={3} borderRadius="md" p={2} bg="rgba(245, 158, 11, 0.12)" border="1px solid rgba(245, 158, 11, 0.25)">
                  <Text fontSize="sm" color="warning" fontWeight={900}>
                    Atenção: acima do recomendado
                  </Text>
                  <Text fontSize="sm" color="warning" opacity={0.95}>
                    Você ultrapassou {maxDaysForModal} dias neste ciclo. Isso não bloqueia a criação — é um alerta para acompanhamento.
                  </Text>
                </Box>
              ) : null}
            </Box>

            {modalError ? (
              <Text mt={3} color="danger" fontSize="sm" fontWeight={700}>
                {modalError}
              </Text>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={() => {
                if (!creating) {
                  onCreateClose();
                  resetModal();
                }
              }}
              isDisabled={creating}
            >
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleCreate} isLoading={creating} isDisabled={creating}>
              Criar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={detailsOpen}
        onClose={() => {
          onDetailsClose();
          setSelectedEvent(null);
          setIsEditing(false);
        }}
        isCentered
        size="md"
      >
        <ModalOverlay />
        <ModalContent bg="surface" borderRadius="2xl" overflow="hidden" borderColor="borderPrimary" borderWidth="1px" mx={{ base: 2, md: 0 }}>
          <ModalHeader color="textPrimary" borderBottomWidth="1px" borderColor="borderPrimary" mb={4}>
            <Flex align="center" gap={2}>
              <Icon as={AiOutlineCalendar} color="secondary" />
              <Text>Detalhes do período</Text>
            </Flex>
          </ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontSize="sm" color="textSecondary" mb={1}>
                  Usuário
                </Text>
                <Text fontWeight={900} color="textPrimary">
                  {selectedEvent?.extendedProps?.userName || "—"}
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" color="textSecondary" mb={1}>
                  Categoria
                </Text>
                {isEditing ? (
                  <Select
                    variant="outline"
                    bg="surfaceRaised"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    disabled={editLoading}
                    sx={{
                      "& > option": {
                        background: "var(--chakra-colors-surface)",
                  color: "var(--chakra-colors-textPrimary)",
                      },
                    }}
                  >
                    {categoriesToRender.map((c) => (
                      <option key={c.id || c.ID} value={c.name || c.NAME} style={{ backgroundColor: "var(--chakra-colors-surface)", color: "var(--chakra-colors-textPrimary)" }}>
                        {c.name || c.NAME}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Text fontWeight={900} color="textPrimary">
                    {selectedEvent?.extendedProps?.category || "Férias"}
                  </Text>
                )}
              </Box>

              {isEditing ? (
                <>
                  <Box>
                    <Text fontSize="sm" color="textSecondary" mb={1}>
                      Altere o Início
                    </Text>
                    <Input
                      type="date"
                      value={editStart}
                      onChange={(e) => setEditStart(e.target.value)}
                      variant="outline"
                      borderColor="borderPrimary"
                      color="textPrimary"
                      bg="surfaceRaised"
                      disabled={editLoading}
                      sx={{
                        "&::-webkit-calendar-picker-indicator": {
                          filter: "var(--calendar-picker-filter, invert(52%) sepia(90%) saturate(2000%) hue-rotate(110deg) brightness(100%) contrast(105%))",
                          cursor: "pointer",
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="textSecondary" mb={1}>
                      Altere o Fim
                    </Text>
                    <Input
                      type="date"
                      value={editEnd}
                      onChange={(e) => setEditEnd(e.target.value)}
                      variant="outline"
                      borderColor="borderPrimary"
                      color="textPrimary"
                      bg="surfaceRaised"
                      disabled={editLoading}
                      sx={{
                        "&::-webkit-calendar-picker-indicator": {
                          filter: "var(--calendar-picker-filter, invert(52%) sepia(90%) saturate(2000%) hue-rotate(110deg) brightness(100%) contrast(105%))",
                          cursor: "pointer",
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="textMuted">
                      O comentário original será mantido.
                    </Text>
                  </Box>
                </>
              ) : (
                <Flex justify="space-between">
                  <Box>
                    <Text fontSize="sm" color="textSecondary" mb={1}>
                      Início
                    </Text>
                    <Text fontWeight={800} color="textPrimary">
                      {fmtBR(detailsStartISO)}
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="sm" color="textSecondary" mb={1}>
                      Fim
                    </Text>
                    <Text fontWeight={800} color="textPrimary">
                      {fmtBR(detailsEndISO)}
                    </Text>
                  </Box>
                </Flex>
              )}

              {!isEditing && (
                <Box>
                  <Text fontSize="sm" color="textSecondary" mb={1}>
                    Comentário
                  </Text>
                  <Text color="textPrimary">
                    {selectedEvent?.extendedProps?.raw?.comment || selectedEvent?.extendedProps?.raw?.Comment || "—"}
                  </Text>
                </Box>
              )}

              {!selectedCanDelete && !isEditing ? (
                <Box borderRadius="md" p={2} bg="rgba(245,158,11,0.10)" border="1px solid rgba(245,158,11,0.22)">
                  <Text color="warning" fontSize="sm" fontWeight={800}>
                    Período bloqueado
                  </Text>
                  <Text color="warning" fontSize="sm" opacity={0.95}>
                    Esse período já ficou no passado e não pode ser excluído (exceto admin).
                  </Text>
                </Box>
              ) : null}
            </VStack>
          </ModalBody>
          <ModalFooter>
            {isEditing ? (
              <HStack spacing={3}>
                <Button variant="ghost" onClick={handleCancelEdit} isDisabled={editLoading}>
                  Cancelar
                </Button>
                <Button colorScheme="green" onClick={handleSaveEdit} isLoading={editLoading}>
                  Salvar
                </Button>
              </HStack>
            ) : (
              <HStack spacing={3}>
                {selectedEvent && (
                  <Button
                    colorScheme="red"
                    variant="ghost"
                    isDisabled={!selectedCanDelete || selectedDeleting}
                    isLoading={selectedDeleting}
                    loadingText="Excluindo"
                    onClick={() => handleDeleteEvent(selectedEvent)}
                  >
                    Excluir
                  </Button>
                )}

                <Button
                  leftIcon={<Icon as={AiOutlineEdit} />}
                  variant="outline"
                  colorScheme="gray"
                  color="textPrimary"
                  onClick={handleStartEdit}
                  isDisabled={selectedDeleting}
                  title="Alterar período"
                  _hover={{ bg: "surfaceHover" }}
                >
                  Editar
                </Button>

                <Button
                  colorScheme="green"
                  onClick={() => {
                    onDetailsClose();
                    setSelectedEvent(null);
                  }}
                >
                  Fechar
                </Button>
              </HStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={createSprintOpen}
        onClose={() => {
          if (!creating) {
            onCreateSprintClose();
          }
        }}
        isCentered
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent w="100%" maxW={{ base: "96vw", md: "860px" }} borderRadius="2xl" bg="surface" borderColor="borderPrimary" borderWidth="1px">
          <ModalHeader color="textPrimary" borderBottomWidth="1px" borderColor="borderPrimary" mb={4}>
            <Flex align="center" gap={2}>
              <Icon as={AiOutlineCalendar} color="secondary" />
              <Text>Criar Sprint</Text>
            </Flex>
          </ModalHeader>
          <ModalBody>
            {isAdmin && (
              <>
                <Text fontSize="sm" mb={2} color="textSecondary">
                  Quem está salvando esse evento (admin)
                </Text>
                <Input
                  variant="outline"
                  borderColor="rgba(2, 234, 117, 0.3)"
                  borderWidth="1px"
                  bg="surface"
                  _hover={{ borderColor: "secondary" }}
                  _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
                  placeholder="Digite para pesquisar (opcional)"
                  value={sprintEmployeePick}
                  onChange={(e) => onSprintEmployeePickChange(e.target.value)}
                  list="sprintEmployeeOptions"
                  mb={3}
                  disabled={creating}
                />
                <datalist id="sprintEmployeeOptions">
                  {employees.map((e) => (
                    <option key={e.id} value={`${e.name} <${e.email}>`} />
                  ))}
                </datalist>
              </>
            )}

            <Text fontSize="sm" mb={2} color="textSecondary">
              Qual sprint estamos
            </Text>
            <Input
              type="number"
              variant="outline"
              borderColor="rgba(2, 234, 117, 0.3)"
              borderWidth="1px"
              bg="surface"
              _hover={{ borderColor: "secondary" }}
              _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
              value={sprintNumber}
              onChange={(e) => setSprintNumber(Number(e.target.value))}
              mb={3}
              disabled={creating}
            />

            <Text fontSize="sm" mb={2} color="textSecondary">
              Data início
            </Text>
            <Input
              type="date"
              variant="outline"
              borderColor="rgba(2, 234, 117, 0.3)"
              borderWidth="1px"
              bg="surface"
              _hover={{ borderColor: "secondary" }}
              _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
              value={sprintStartDate}
              onChange={(e) => setSprintStartDate(e.target.value)}
              mb={3}
              disabled={creating}
              sx={{
                "&::-webkit-calendar-picker-indicator": {
                  filter: "invert(52%) sepia(90%) saturate(2000%) hue-rotate(110deg) brightness(100%) contrast(105%)",
                  cursor: "pointer",
                },
              }}
            />

            <Text fontSize="sm" mb={2} color="textSecondary">
              Data fim
            </Text>
            <Input
              type="date"
              variant="outline"
              borderColor="rgba(2, 234, 117, 0.3)"
              borderWidth="1px"
              bg="surface"
              _hover={{ borderColor: "secondary" }}
              _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
              value={sprintEndDate}
              onChange={(e) => setSprintEndDate(e.target.value)}
              mb={3}
              disabled={creating}
              sx={{
                "&::-webkit-calendar-picker-indicator": {
                  filter: "invert(52%) sepia(90%) saturate(2000%) hue-rotate(110deg) brightness(100%) contrast(105%)",
                  cursor: "pointer",
                },
              }}
            />

            <Text fontSize="sm" mb={2} color="textSecondary">
              Comentário (opcional)
            </Text>
            <Textarea
              variant="outline"
              borderColor="rgba(2, 234, 117, 0.3)"
              borderWidth="1px"
              bg="surface"
              _hover={{ borderColor: "secondary" }}
              _focus={{ borderColor: "secondary", boxShadow: "0 0 0 1px rgba(2, 234, 117, 0.4)" }}
              placeholder="Ex.: foco, chip varejo, Bold..."
              value={sprintComment}
              onChange={(e) => setSprintComment(e.target.value)}
              mb={3}
              disabled={creating}
              rows={3}
              resize="vertical"
            />

            {sprintModalError ? (
              <Text mt={3} color="danger" fontSize="sm" fontWeight={700}>
                {sprintModalError}
              </Text>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={onCreateSprintClose}
              isDisabled={creating}
            >
              Cancelar
            </Button>
            <Button colorScheme="purple" onClick={handleCreateSprint} isLoading={creating} isDisabled={creating}>
              Criar Sprint
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={sprintWarnOpen} onClose={onSprintWarnClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" bg="surface" borderColor="borderPrimary" borderWidth="1px" overflow="hidden">
          <ModalHeader color="textPrimary" borderBottomWidth="1px" borderColor="borderPrimary" py={4}>
            <Flex align="center" gap={2}>
              <Icon as={AiOutlineWarning} color="orange.400" />
              <Text fontSize="lg" fontWeight="900">Atenção</Text>
            </Flex>
          </ModalHeader>
          <ModalBody py={6}>
            <VStack align="stretch" spacing={4}>
              <Box 
                p={4} 
                borderRadius="xl" 
                bg="rgba(221, 107, 32, 0.05)" 
                border="1px solid rgba(221, 107, 32, 0.15)"
                borderLeft="4px solid"
                borderLeftColor="orange.400"
              >
                <VStack align="stretch" spacing={3}>
                  {sprintWarnMessages.map((msg, i) => (
                    <Flex key={i} align="flex-start" gap={2}>
                      <Text color="orange.400" fontSize="sm" mt="2px">•</Text>
                      <Text fontSize="sm" color="textSecondary" fontWeight={500} lineHeight="tall">
                        {msg}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
              
              <Text color="textPrimary" fontWeight={800} fontSize="sm" textAlign="center" pt={2}>
                Deseja prosseguir com a criação da Sprint?
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor="borderPrimary" py={3}>
            <HStack spacing={3} width="100%" justify="flex-end">
              <Button 
                variant="ghost" 
                color="textSecondary" 
                onClick={onSprintWarnClose} 
                isDisabled={creating}
                _hover={{ bg: "surfaceHover", color: "textPrimary" }}
              >
                Voltar e Ajustar
              </Button>
              <Button 
                colorScheme="purple" 
                onClick={submitSprint} 
                isLoading={creating} 
                isDisabled={creating}
                px={6}
              >
                Sim, criar Sprint
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box >
  );
}
